import React, { useEffect, useState } from 'react';
import {
  Table, Tag, Typography, Select, Button, Alert, Card, Modal, Form, Input,
  message, Drawer, Descriptions, Radio, Divider, Space, Upload,
} from 'antd';
import { PlusOutlined, UploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import {
  getProjects, getSnags, createSnag, getContractors,
  closePunchPoint, escalatePunchPoint, assignPunchPoint, rectifyPunchPoint, rejectPunchPointRectification,
} from '../services/api';
import { useNavigation } from '../context/NavigationContext';
import RecordTimeline from '../components/RecordTimeline';
import StagePhotos from '../components/StagePhotos';
const { Title, Text } = Typography;
const { TextArea } = Input;

interface Project {
  id: string;
  code: string;
  name: string;
}

interface Contractor {
  id: string;
  name: string;
  code: string;
}

// Media files (photos) are served from the Django backend's /media/ path,
// not the /api/v1 prefix, so strip that off to build a browsable URL.

type ActionType = 'close' | 'escalate' | 'assign' | 'rectify' | 'reject';

const SnagRegisterPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [snags, setSnags] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [contractors, setContractors] = useState<Contractor[]>([]);

  const [raiseModalOpen, setRaiseModalOpen] = useState(false);
  const [raiseSubmitting, setRaiseSubmitting] = useState(false);
  const [raiseForm] = Form.useForm();
  const [isHoldPoint, setIsHoldPoint] = useState(false);
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSnag, setSelectedSnag] = useState<any>(null);

  const [actionModal, setActionModal] = useState<{ type: ActionType; snagId: string } | null>(null);
  const [closureRemarks, setClosureRemarks] = useState('');
  const [closureEvidence, setClosureEvidence] = useState<File | null>(null);
  const [escalateTo, setEscalateTo] = useState<string>('');
  const [escalationComments, setEscalationComments] = useState('');
  const [assignTo, setAssignTo] = useState<string>('');
  const [rectificationRemarks, setRectificationRemarks] = useState('');
  const [rejectionRemarks, setRejectionRemarks] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);

  useEffect(() => {
    loadProjects();
    loadContractors();
  }, []);

  const { pendingTarget, clearPendingTarget } = useNavigation();
  const target = pendingTarget?.recordType === 'SNAG' ? pendingTarget : null;

  // Opens the record that brought the user here through a clickable ID, once the list has loaded.
  useEffect(() => {
    if (!target || loading) return;
    const match = snags.find((s) => s.display_id === target.recordId);
    if (match) {
      openDrawer(match);
      clearPendingTarget();
    }
  }, [snags, loading, target, clearPendingTarget]);

  // A pending target only belongs to the visit it was set for.
  useEffect(() => clearPendingTarget, [clearPendingTarget]);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (response.data.length > 0) {
        const initial = response.data.find((p: any) => p.id === target?.projectId) || response.data[0];
        setSelectedProject(initial.id);
        loadSnags(initial.id);
      }
    } catch (err) {
      setError('Failed to load projects.');
    }
  };

  const loadContractors = async () => {
    try {
      const response = await getContractors();
      setContractors(response.data);
    } catch (err) {
      // non-fatal — only blocks the Assign action, not the rest of the page
    }
  };

  const loadSnags = async (projectId: string) => {
    setLoading(true);
    try {
      const response = await getSnags(projectId);
      setSnags(response.data);
    } catch (err) {
      setError('Failed to load snags.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
    loadSnags(value);
  };

  // ---------- Raise Snag modal (independent raising) ----------

  const openRaiseModal = () => {
    raiseForm.resetFields();
    setIsHoldPoint(false);
    setEvidenceFile(null);
    setRaiseModalOpen(true);
  };

  const closeRaiseModal = () => setRaiseModalOpen(false);

  const handleRaiseSubmit = async () => {
    try {
      const values = await raiseForm.validateFields();

      if (!evidenceFile) {
        message.error('A raise-time evidence photo is required.');
        return;
      }

      setRaiseSubmitting(true);

      const formData = new FormData();
      formData.append('activity_type', values.activity_type);
      formData.append('location_reference', values.location_reference);
      formData.append('item_name', values.item_name);
      formData.append('description', values.description || '');
      formData.append('is_hold_point', String(isHoldPoint));
      formData.append('evidence', evidenceFile);

      await createSnag(selectedProject, formData);
      message.success('Snag raised.');
      setRaiseModalOpen(false);
      loadSnags(selectedProject);
    } catch (err: any) {
      if (err?.errorFields) {
        return;
      }
      const apiMessage = err?.response?.data?.error || 'Failed to raise snag.';
      message.error(apiMessage);
    } finally {
      setRaiseSubmitting(false);
    }
  };

  // ---------- Detail drawer ----------

  const openDrawer = (record: any) => {
    setSelectedSnag(record);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedSnag(null);
  };

  // ---------- Actions ----------

  const openActionModal = (type: ActionType, snag: any) => {
    setClosureRemarks('');
    setClosureEvidence(null);
    setEscalateTo('');
    setEscalationComments('');
    setAssignTo('');
    setRectificationRemarks('');
    setRejectionRemarks('');
    setActionModal({ type, snagId: snag.id });
  };

  const closeActionModal = () => setActionModal(null);

  const handleClose = async () => {
    if (!actionModal) return;
    if (!closureEvidence) {
      message.error('Closure evidence photo is required.');
      return;
    }
    setActionSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('closure_remarks', closureRemarks);
      formData.append('evidence', closureEvidence);
      await closePunchPoint(actionModal.snagId, formData);
      message.success('Snag cleared.');
      const snagId = actionModal.snagId;
      closeActionModal();
      await loadSnags(selectedProject);
      if (selectedSnag?.id === snagId) setDrawerOpen(false);
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to close snag.');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleEscalate = async () => {
    if (!actionModal) return;
    if (!escalateTo) {
      message.error('Select who to escalate to.');
      return;
    }
    setActionSubmitting(true);
    try {
      await escalatePunchPoint(actionModal.snagId, {
        escalated_to: escalateTo,
        escalation_comments: escalationComments,
      });
      message.success('Snag escalated.');
      closeActionModal();
      loadSnags(selectedProject);
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to escalate snag.');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleAssign = async () => {
    if (!actionModal) return;
    if (!assignTo) {
      message.error('Select a contractor to assign to.');
      return;
    }
    setActionSubmitting(true);
    try {
      await assignPunchPoint(actionModal.snagId, { assigned_to: assignTo });
      message.success('Snag assigned.');
      closeActionModal();
      loadSnags(selectedProject);
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to assign snag.');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleRectify = async () => {
    if (!actionModal) return;
    setActionSubmitting(true);
    try {
      await rectifyPunchPoint(actionModal.snagId, { rectification_remarks: rectificationRemarks });
      message.success('Marked as rectified.');
      closeActionModal();
      loadSnags(selectedProject);
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to mark as rectified.');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!actionModal) return;
    if (!rejectionRemarks.trim()) {
      message.error('Rejection remarks are required.');
      return;
    }
    setActionSubmitting(true);
    try {
      await rejectPunchPointRectification(actionModal.snagId, { rejection_remarks: rejectionRemarks });
      message.success('Sent back to contractor.');
      closeActionModal();
      loadSnags(selectedProject);
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to reject rectification.');
    } finally {
      setActionSubmitting(false);
    }
  };

  // ---------- Rendering helpers ----------

  const statusTag = (status: string) => {
    if (status === 'OPEN') return <Tag color="orange">Open</Tag>;
    if (status === 'ASSIGNED') return <Tag color="blue">Assigned</Tag>;
    if (status === 'RECTIFIED') return <Tag color="purple">Rectified</Tag>;
    if (status === 'ESCALATED') return <Tag color="volcano">Escalated</Tag>;
    if (status === 'CLOSED') return <Tag icon={<CheckCircleOutlined />} color="green">Closed</Tag>;
    return <Tag>{status}</Tag>;
  };

  const agingDays = (raisedAt: string, status: string) => {
    if (status === 'CLOSED') return null;
    const diffMs = Date.now() - new Date(raisedAt).getTime();
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  };

  const actionButtonsFor = (snag: any) => {
    switch (snag.status) {
      case 'OPEN':
        return (
          <Space wrap>
            <Button size="small" type="primary" style={{ background: '#1B2A4A' }} onClick={() => openActionModal('close', snag)}>Close</Button>
            <Button size="small" onClick={() => openActionModal('escalate', snag)}>Escalate</Button>
            <Button size="small" onClick={() => openActionModal('assign', snag)}>Assign</Button>
          </Space>
        );
      case 'ASSIGNED':
        return (
          <Space wrap>
            <Button size="small" type="primary" style={{ background: '#1B2A4A' }} onClick={() => openActionModal('rectify', snag)}>Mark Rectified</Button>
            <Button size="small" onClick={() => openActionModal('close', snag)}>Close</Button>
            <Button size="small" onClick={() => openActionModal('escalate', snag)}>Escalate</Button>
          </Space>
        );
      case 'RECTIFIED':
        return (
          <Space wrap>
            <Button size="small" type="primary" style={{ background: '#1B2A4A' }} onClick={() => openActionModal('close', snag)}>Clear</Button>
            <Button size="small" danger onClick={() => openActionModal('reject', snag)}>Reject Back</Button>
          </Space>
        );
      case 'ESCALATED':
        return (
          <Space wrap>
            <Button size="small" type="primary" style={{ background: '#1B2A4A' }} onClick={() => openActionModal('close', snag)}>Close</Button>
          </Space>
        );
      default:
        return null;
    }
  };

  const columns = [
    { title: 'Snag ID', dataIndex: 'display_id', key: 'display_id', render: (text: string) => <Text strong style={{ color: '#1B2A4A' }}>{text}</Text> },
    {
      title: 'Source',
      key: 'source',
      render: (_: any, record: any) =>
        record.source === 'WIR'
          ? <Tag color="geekblue">WIR — {record.wir_object_id}</Tag>
          : <Tag color="default">Independent</Tag>,
    },
    { title: 'Item', dataIndex: 'item_name', key: 'item_name' },
    { title: 'Location', dataIndex: 'location_reference', key: 'location_reference' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: statusTag },
    {
      title: 'Assigned To',
      key: 'assigned_to_name',
      render: (_: any, record: any) => record.assigned_to_name || <Text type="secondary">—</Text>,
    },
    {
      title: 'Raised',
      dataIndex: 'raised_at',
      key: 'raised_at',
      render: (date: string) => new Date(date).toLocaleDateString('en-IN'),
    },
    {
      title: 'Aging',
      key: 'aging',
      render: (_: any, record: any) => {
        const days = agingDays(record.raised_at, record.status);
        if (days === null) return <Text type="secondary">—</Text>;
        return <Tag color={days > 14 ? 'red' : days > 7 ? 'gold' : 'default'}>{days}d</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <div onClick={(e) => e.stopPropagation()}>{actionButtonsFor(record)}</div>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, color: '#1B2A4A' }}>
          Snag Register
        </Title>
        <div style={{ display: 'flex', gap: 12 }}>
          <Select
            value={selectedProject}
            onChange={handleProjectChange}
            style={{ width: 250 }}
            options={projects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
          />
          <Button type="primary" icon={<PlusOutlined />} style={{ background: '#1B2A4A' }} onClick={openRaiseModal} disabled={!selectedProject}>
            Raise Snag
          </Button>
        </div>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      <Card style={{ borderRadius: 12 }}>
        <Table
          columns={columns}
          dataSource={snags}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No snags found for this project.' }}
          onRow={(record) => ({
            onClick: () => openDrawer(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {/* ---------- Detail Drawer ---------- */}
      <Drawer
        title={selectedSnag?.display_id || 'Snag Details'}
        open={drawerOpen}
        onClose={closeDrawer}
        width={620}
      >
        {selectedSnag && (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Source">
                {selectedSnag.source === 'WIR' ? `WIR — ${selectedSnag.wir_object_id}` : 'Independent Inspection'}
              </Descriptions.Item>
              <Descriptions.Item label="Activity Type">{selectedSnag.activity_type}</Descriptions.Item>
              <Descriptions.Item label="Location">{selectedSnag.location_reference}</Descriptions.Item>
              <Descriptions.Item label="Item">{selectedSnag.item_name}</Descriptions.Item>
              {selectedSnag.description && <Descriptions.Item label="Description">{selectedSnag.description}</Descriptions.Item>}
              <Descriptions.Item label="Status">{statusTag(selectedSnag.status)}</Descriptions.Item>
              <Descriptions.Item label="Hold Point">{selectedSnag.is_hold_point ? 'Yes' : 'No'}</Descriptions.Item>
              <Descriptions.Item label="Raised">{new Date(selectedSnag.raised_at).toLocaleString('en-IN')}</Descriptions.Item>
              {selectedSnag.assigned_to_name && (
                <Descriptions.Item label="Assigned To">
                  {selectedSnag.assigned_to_name} — {new Date(selectedSnag.assigned_at).toLocaleString('en-IN')}
                </Descriptions.Item>
              )}
              {selectedSnag.rectified_at && (
                <Descriptions.Item label="Rectified">
                  {new Date(selectedSnag.rectified_at).toLocaleString('en-IN')} — {selectedSnag.rectification_remarks}
                </Descriptions.Item>
              )}
              {selectedSnag.rejected_at && (
                <Descriptions.Item label="Last Rejected">
                  {new Date(selectedSnag.rejected_at).toLocaleString('en-IN')} — {selectedSnag.rejection_remarks}
                </Descriptions.Item>
              )}
              {selectedSnag.escalated_at && (
                <Descriptions.Item label="Escalated">
                  To {selectedSnag.escalated_to} — {new Date(selectedSnag.escalated_at).toLocaleString('en-IN')} — {selectedSnag.escalation_comments}
                </Descriptions.Item>
              )}
              {selectedSnag.closed_at && (
                <Descriptions.Item label="Closed">
                  {new Date(selectedSnag.closed_at).toLocaleString('en-IN')} — {selectedSnag.closure_remarks}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider>Photos</Divider>
            <StagePhotos attachments={selectedSnag.attachments} />

            <Divider>Timeline</Divider>
            <RecordTimeline recordId={selectedSnag.display_id} projectId={selectedProject} />

            <Divider />
            {actionButtonsFor(selectedSnag)}
          </>
        )}
      </Drawer>

      {/* ---------- Raise Snag Modal ---------- */}
      <Modal
        title="Raise Snag (Independent Inspection)"
        open={raiseModalOpen}
        onCancel={closeRaiseModal}
        onOk={handleRaiseSubmit}
        confirmLoading={raiseSubmitting}
        okText="Raise Snag"
        width={620}
      >
        <Form form={raiseForm} layout="vertical" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="activity_type" label="Activity Type" style={{ flex: 1 }} rules={[{ required: true, message: 'Activity type is required' }]}>
              <Input placeholder="e.g. Masonry, Finishes, MEP" />
            </Form.Item>
            <Form.Item name="location_reference" label="Location" style={{ flex: 1 }} rules={[{ required: true, message: 'Location is required' }]}>
              <Input placeholder="e.g. Tower B, Floor 4" />
            </Form.Item>
          </div>
          <Form.Item name="item_name" label="Snag / Item" rules={[{ required: true, message: 'Item name is required' }]}>
            <Input placeholder="e.g. Plaster crack near window" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="What's wrong, observed condition, etc." />
          </Form.Item>
          <Form.Item label="Judgment call">
            <Radio.Group value={isHoldPoint} onChange={(e) => setIsHoldPoint(e.target.value)}>
              <Radio value={true}>Hold Point (blocks work)</Radio>
              <Radio value={false}>Snag-clearable</Radio>
            </Radio.Group>
          </Form.Item>
          <Upload
            beforeUpload={(file) => { setEvidenceFile(file); return false; }}
            onRemove={() => setEvidenceFile(null)}
            maxCount={1}
            accept="image/*"
          >
            <Button icon={<UploadOutlined />}>
              {evidenceFile ? evidenceFile.name : 'Upload Evidence Photo (required)'}
            </Button>
          </Upload>
        </Form>
      </Modal>

      {/* ---------- Action Modal ---------- */}
      <Modal
        title={
          actionModal?.type === 'close' ? 'Close / Clear Snag' :
          actionModal?.type === 'escalate' ? 'Escalate Snag' :
          actionModal?.type === 'assign' ? 'Assign to Contractor' :
          actionModal?.type === 'rectify' ? 'Mark Rectified' :
          'Reject Rectification'
        }
        open={!!actionModal}
        onCancel={closeActionModal}
        onOk={
          actionModal?.type === 'close' ? handleClose :
          actionModal?.type === 'escalate' ? handleEscalate :
          actionModal?.type === 'assign' ? handleAssign :
          actionModal?.type === 'rectify' ? handleRectify :
          handleReject
        }
        confirmLoading={actionSubmitting}
        okText={
          actionModal?.type === 'close' ? 'Close' :
          actionModal?.type === 'escalate' ? 'Escalate' :
          actionModal?.type === 'assign' ? 'Assign' :
          actionModal?.type === 'rectify' ? 'Mark Rectified' :
          'Send Back'
        }
        okButtonProps={actionModal?.type === 'reject' ? { danger: true } : {}}
      >
        {actionModal?.type === 'close' && (
          <>
            <TextArea
              placeholder="Closure remarks"
              value={closureRemarks}
              onChange={(e) => setClosureRemarks(e.target.value)}
              rows={3}
              style={{ marginBottom: 12 }}
            />
            <Upload
              beforeUpload={(file) => { setClosureEvidence(file); return false; }}
              onRemove={() => setClosureEvidence(null)}
              maxCount={1}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>
                {closureEvidence ? closureEvidence.name : 'Upload Closure Evidence (required)'}
              </Button>
            </Upload>
          </>
        )}

        {actionModal?.type === 'escalate' && (
          <>
            <Form.Item label="Escalate To" style={{ marginBottom: 12 }}>
              <Select
                placeholder="Select target"
                value={escalateTo || undefined}
                onChange={setEscalateTo}
                options={[
                  { value: 'PMC', label: 'PMC' },
                  { value: 'CONTRACTOR', label: 'Contractor' },
                  { value: 'CLIENT', label: 'Client' },
                ]}
              />
            </Form.Item>
            <TextArea
              placeholder="Escalation comments"
              value={escalationComments}
              onChange={(e) => setEscalationComments(e.target.value)}
              rows={3}
            />
          </>
        )}

        {actionModal?.type === 'assign' && (
          <Form.Item label="Assign To Contractor">
            <Select
              placeholder="Select contractor"
              value={assignTo || undefined}
              onChange={setAssignTo}
              options={contractors.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
            />
          </Form.Item>
        )}

        {actionModal?.type === 'rectify' && (
          <TextArea
            placeholder="Rectification remarks (what was done, recorded on contractor's behalf after site verification)"
            value={rectificationRemarks}
            onChange={(e) => setRectificationRemarks(e.target.value)}
            rows={3}
          />
        )}

        {actionModal?.type === 'reject' && (
          <TextArea
            placeholder="Why this isn't actually fixed (required) — sends it back to the assigned contractor"
            value={rejectionRemarks}
            onChange={(e) => setRejectionRemarks(e.target.value)}
            rows={3}
          />
        )}
      </Modal>
    </div>
  );
};

export default SnagRegisterPage;